import { ChangeEvent, useCallback, useRef, useState } from 'react';

import { useTranslation } from '@hooks/useTranslations';
import { CreditCardBrand, Nullable } from '@types-internal';
import { catchFatalError } from '@utils/utils';

import { BinLookupResult } from './useAdyenSecureFields';
import {
  BancontactAllErrors,
  areBancontactIdenticalCardErrors,
  DEFAULT_BANCONTACT_ADYEN_STATE,
  DEFAULT_BANCONTACT_ERRORS,
  getFromAdyenCardBrand,
  getLastFourDigits,
  maybeErrorForHolderName,
  parseStateOnChange,
  getFocusedElement,
  BancontactAdyenCreditCardFormData,
  isBancontactAdyenStateValid,
  BancontactAdyenState,
  convertBancontactAdyenState,
  addRequiredMessageIfNeeded,
} from './useBancontactAdyenState.static';

type Options = { isModernize: boolean };

/**
 * Because we use the 'securedfields' of AdyenCheckout, we need to pass several callbacks to AdyenCheckout in order to gather the encrypted information.
 *
 * This hook is providing these necessary callbacks and collect everything into a ref.
 *
 * In addition, it also collects the holderName and the storeInVault data, as the associated inputs are not covered by the 'securedfields' approach.
 *
 * For validation and retrieval of the encrypted data, this hook provides the function 'computeAdyenCreditCardFormData'.
 */
const useBancontactAdyenState = (
  onFatalError: (error: unknown) => void,
  { isModernize }: Options = { isModernize: false },
) => {
  const bancontactAdyenStateRef = useRef<BancontactAdyenState>({
    ...DEFAULT_BANCONTACT_ADYEN_STATE, // deep copy: the reference to the DEFAULT_ADYEN_STATE constant should not be used since we are modifying the object in this hook
    paymentMethod: { ...DEFAULT_BANCONTACT_ADYEN_STATE.paymentMethod },
  });
  const [storeInVault, setStoreInVault] = useState(DEFAULT_BANCONTACT_ADYEN_STATE.storeInVault);
  const [creditCardBrand, setCreditCardBrand] = useState<CreditCardBrand | null>(
    DEFAULT_BANCONTACT_ADYEN_STATE.creditCardBrand,
  );
  // The detected and supported card brands from the BIN lookup operation.
  const [detectedCardBrands, setDetectedCardBrands] = useState<Nullable<CreditCardBrand>[]>([]);
  const [focusedElement, setFocusedElement] = useState<string | null>(null);
  const [errors, setErrors] = useState<BancontactAllErrors>({ ...DEFAULT_BANCONTACT_ERRORS });
  const { t } = useTranslation();

  const requiredMessage = t('checkout.form.required.error');
  const requiredMessagesNew = {
    requiredNameMessage: t('checkout.form.name.required.error'),
    requiredMonthMessage: t('checkout.form.expiration.month.required.error'),
    requiredNumberMessage: t('checkout.form.number.required.error'),
    invalidYearMessage: t('checkout.payment.creditcard.expiry_year.invalid'),
    requiredYearMessage: t('checkout.form.expiration.year.required.error'),
  };
  // The holderName is not managed by Adyen so we keep track of the changes ourselves.
  const onBancontactHolderNameChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    catchFatalError(onFatalError, (event: ChangeEvent<HTMLInputElement>) => {
      const holderName = event.target.value;
      bancontactAdyenStateRef.current.paymentMethod.holderName = holderName;

      setErrors((errors) => {
        const errorForHolderName = maybeErrorForHolderName(holderName, t, true);
        // if no change in errors, return the given 'errors' to tell React not to re-render
        return errors.holderName === errorForHolderName
          ? errors
          : { ...errors, holderName: errorForHolderName };
      });
    }),
    [bancontactAdyenStateRef, onFatalError],
  );

  // We use the Adyen 'onFieldValid' callback only to get the last four digits of the card number.
  // The actual validity of fields is managed with 'onChange' below.
  const onBancontactFieldValid = useCallback(
    catchFatalError(onFatalError, (data: unknown) => {
      const nullableLastFour = getLastFourDigits(data);
      // as 'onFieldValid' is called for every input but only the 'encryptedCardNumber' has the last four digits, we update the state only when needed
      if (nullableLastFour !== null) {
        bancontactAdyenStateRef.current.lastFour = nullableLastFour;
      }
    }),
    [bancontactAdyenStateRef, onFatalError],
  );

  // 'storeInVault' is also tracked with a hook to re-render the checkbox when storeInVault has changed
  const onBancontactStoreInVaultChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    catchFatalError(onFatalError, (event: ChangeEvent<HTMLInputElement>) => {
      const storeInVault = event.target.checked;
      bancontactAdyenStateRef.current.storeInVault = storeInVault;
      setStoreInVault(storeInVault);
    }),
    [bancontactAdyenStateRef, onFatalError],
  );

  // Adyen has the specific callback 'onBrand' for when it detects the brand of the card.
  // And 'brand' is also tracked with a hook to re-render the card icons and adapt the UI to Amex cards.
  const onBancontactBrand = useCallback(
    catchFatalError(onFatalError, (brandData: unknown) => {
      const creditCardBrand = getFromAdyenCardBrand(brandData);
      bancontactAdyenStateRef.current.creditCardBrand = creditCardBrand;
      setCreditCardBrand(creditCardBrand);
    }),
    [bancontactAdyenStateRef, onFatalError],
  );

  /* The callback to be used to perform Adyen secured fields "onBinLookup", the event will be fired
   * when the users complete input the card BIN (first 6 to 8 digits). */
  const onBancontactBinLookup = useCallback((binLookupResult: BinLookupResult) => {
    const cardBrands = binLookupResult.supportedBrands?.map((brand) =>
      getFromAdyenCardBrand(brand),
    );
    setDetectedCardBrands(cardBrands || []);
  }, []);

  // Adyen provides the 'onChange' callback that is triggered when the customer entered his card information.
  // It contains the bulk of the data we need, as well as the validation errors of the fields, if any.
  const onChange = useCallback(
    catchFatalError(onFatalError, (state: unknown) => {
      const parsedState = parseStateOnChange(state);
      bancontactAdyenStateRef.current.isValid = parsedState.isValid;
      Object.assign(bancontactAdyenStateRef.current.paymentMethod, parsedState.paymentMethod);

      setErrors((errors) => {
        // if no change in errors, return the given 'errors' to tell React not to re-render
        return areBancontactIdenticalCardErrors(errors, parsedState.errors)
          ? errors
          : Object.assign(parsedState.errors, {
              holderName: errors.holderName,
            });
      });
    }),
    [bancontactAdyenStateRef, onFatalError],
  );

  const onFocus = useCallback(
    catchFatalError(onFatalError, (data: unknown) => {
      setFocusedElement(getFocusedElement(data));
    }),
    [bancontactAdyenStateRef, onFatalError],
  );

  // Return the state if all is fine: no errors on any field & all the encrypted data is present
  const computeBancontactAdyenCreditCardFormData =
    useCallback((): Nullable<BancontactAdyenCreditCardFormData> => {
      try {
        const state = bancontactAdyenStateRef.current;

        if (isBancontactAdyenStateValid(state)) {
          return convertBancontactAdyenState(state);
        } else {
          setErrors((errors) => {
            const newErrors = addRequiredMessageIfNeeded(
              errors,
              state,
              isModernize ? requiredMessagesNew : requiredMessage,
            );
            // no need to return a new 'errors' object if there's nothing to change
            return Object.keys(newErrors).length === 0 ? errors : { ...errors, ...newErrors };
          });
          return null;
        }
      } catch (error) {
        onFatalError(error);
        return null;
      }
    }, [bancontactAdyenStateRef, requiredMessage, onFatalError]);

  return {
    bancontactAdyenStateRef,
    detectedCardBrands,
    onChange,
    onFieldValid: onBancontactFieldValid,
    onHolderNameChange: onBancontactHolderNameChange,
    storeInVault,
    onStoreInVaultChange: onBancontactStoreInVaultChange,
    focusedElement,
    onFocus,
    creditCardBrand,
    onBrand: onBancontactBrand,
    onBinLookup: onBancontactBinLookup,
    computeBancontactAdyenCreditCardFormData,
    errors,
  };
};

export default useBancontactAdyenState;
